import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import { API_URL, formatDate, getStatusColor, statusLabels } from "../../lib/utils";
import {
    ArrowLeft,
    CheckCircle2,
    Circle,
    Clock,
    AlertCircle,
} from "lucide-react";

const taskStatusIcons = {
    done: <CheckCircle2 className="w-4 h-4 text-green-500" />,
    in_progress: <Clock className="w-4 h-4 text-blue-500" />,
    todo: <Circle className="w-4 h-4 text-gray-400" />,
    blocked: <AlertCircle className="w-4 h-4 text-red-500" />,
};

export const PortalProjectDetailPage = () => {
    const { projectId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch_ = async () => {
            try {
                const res = await fetch(`${API_URL}/portal/projects/${projectId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setProject(await res.json());
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetch_();
    }, [token, projectId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="text-center py-12">
                <p className="text-muted-foreground">Projet introuvable</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate("/portal/projects")}>
                    Retour aux projets
                </Button>
            </div>
        );
    }

    const tasks = project.tasks || [];
    const groupedTasks = {
        todo: tasks.filter((t) => t.status === "todo"),
        in_progress: tasks.filter((t) => t.status === "in_progress"),
        done: tasks.filter((t) => t.status === "done"),
        blocked: tasks.filter((t) => t.status === "blocked"),
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="portal-project-detail">
            <Button variant="ghost" size="sm" onClick={() => navigate("/portal/projects")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux projets
            </Button>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl text-foreground">{project.name}</h1>
                    {project.description && (
                        <p className="text-muted-foreground text-sm mt-1">{project.description}</p>
                    )}
                </div>
                <Badge className={getStatusColor(project.status)}>
                    {statusLabels[project.status] || project.status}
                </Badge>
            </div>

            {/* Progress */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Avancement global</span>
                        <span className="text-sm font-bold text-primary">{project.progress}%</span>
                    </div>
                    <Progress value={project.progress} className="h-3" />
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>{project.completed_tasks} / {project.total_tasks} tâches terminées</span>
                        {project.start_date && <span>Début : {formatDate(project.start_date)}</span>}
                        {project.end_date && <span>Fin prévue : {formatDate(project.end_date)}</span>}
                    </div>
                </CardContent>
            </Card>

            {/* Tasks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(groupedTasks).map(([status, statusTasks]) => (
                    statusTasks.length > 0 && (
                        <Card key={status}>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-heading flex items-center gap-2">
                                    {taskStatusIcons[status]}
                                    {statusLabels[status] || status}
                                    <Badge variant="secondary" className="ml-auto text-xs">{statusTasks.length}</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {statusTasks.map((task) => (
                                    <div
                                        key={task.task_id}
                                        className="flex items-start justify-between p-3 rounded-lg bg-muted/50"
                                    >
                                        <div>
                                            <p className="text-sm font-medium">{task.name}</p>
                                            {task.description && (
                                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                                    {task.description}
                                                </p>
                                            )}
                                        </div>
                                        {task.due_date && (
                                            <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                                {formatDate(task.due_date)}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )
                ))}
                {tasks.length === 0 && (
                    <Card className="col-span-2">
                        <CardContent className="py-8 text-center text-muted-foreground">
                            Aucune tâche définie pour ce projet
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};
